import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import type {
  CreatePartnerInput,
  Partner,
  PartnerLog,
  PartnerLogRepository,
  PartnerRepository,
  Unsubscribe,
} from '@nx/domain';
import { mapPartnerDoc, mapPartnerLogDoc, snapshotData } from './firestoreMappers';

function partnersCol(db: Firestore, eventId: string) {
  return collection(db, 'events', eventId, 'partners');
}

function partnerLogsCol(db: Firestore, eventId: string) {
  return collection(db, 'events', eventId, 'partnerLogs');
}

export class FirebasePartnerRepository implements PartnerRepository {
  constructor(private readonly db: Firestore) {}

  async listByEvent(eventId: string): Promise<Partner[]> {
    return new Promise((resolve, reject) => {
      const unsub = onSnapshot(
        partnersCol(this.db, eventId),
        (snap) => {
          unsub();
          const items = snap.docs.map((d) => mapPartnerDoc(eventId, d.id, snapshotData(d)));
          items.sort((a, b) => (a.logoDisplayOrder ?? 999) - (b.logoDisplayOrder ?? 999));
          resolve(items);
        },
        reject,
      );
    });
  }

  watchByEvent(eventId: string, listener: (partners: Partner[]) => void): Unsubscribe {
    return onSnapshot(partnersCol(this.db, eventId), (snap) => {
      const items = snap.docs.map((d) => mapPartnerDoc(eventId, d.id, snapshotData(d)));
      items.sort((a, b) => (a.logoDisplayOrder ?? 999) - (b.logoDisplayOrder ?? 999));
      listener(items);
    });
  }

  async getById(eventId: string, partnerId: string): Promise<Partner | null> {
    const snap = await getDoc(doc(this.db, 'events', eventId, 'partners', partnerId));
    if (!snap.exists()) return null;
    return mapPartnerDoc(eventId, snap.id, snapshotData(snap));
  }

  async create(eventId: string, input: CreatePartnerInput): Promise<Partner> {
    const ref = await addDoc(partnersCol(this.db, eventId), {
      companyName: input.companyName.trim(),
      contactName: input.contactName?.trim() ?? null,
      contactEmail: input.contactEmail?.trim() ?? null,
      contactPhone: input.contactPhone?.trim() ?? null,
      logoUrl: input.logoUrl ?? null,
      logoDisplayOrder: input.logoDisplayOrder ?? null,
      sponsorshipLevel: input.sponsorshipLevel ?? null,
      collaborationContent: input.collaborationContent?.trim() ?? null,
      status: input.status ?? 'negotiating',
      contractFileUrl: input.contractFileUrl ?? null,
      contractDueDate: input.contractDueDate ?? null,
      monetaryAmount: input.monetaryAmount ?? null,
      resourceExchangeNote: input.resourceExchangeNote?.trim() ?? null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const created = await this.getById(eventId, ref.id);
    if (!created) throw new Error('Partner not found after create');
    return created;
  }

  async update(
    eventId: string,
    partnerId: string,
    input: Partial<CreatePartnerInput>,
  ): Promise<Partner> {
    const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (input.companyName !== undefined) patch.companyName = input.companyName.trim();
    if (input.contactName !== undefined) patch.contactName = input.contactName?.trim() ?? null;
    if (input.contactEmail !== undefined) patch.contactEmail = input.contactEmail?.trim() ?? null;
    if (input.contactPhone !== undefined) patch.contactPhone = input.contactPhone?.trim() ?? null;
    if (input.logoUrl !== undefined) patch.logoUrl = input.logoUrl;
    if (input.logoDisplayOrder !== undefined) patch.logoDisplayOrder = input.logoDisplayOrder;
    if (input.sponsorshipLevel !== undefined) patch.sponsorshipLevel = input.sponsorshipLevel;
    if (input.collaborationContent !== undefined) {
      patch.collaborationContent = input.collaborationContent?.trim() ?? null;
    }
    if (input.status !== undefined) patch.status = input.status;
    if (input.contractFileUrl !== undefined) patch.contractFileUrl = input.contractFileUrl;
    if (input.contractDueDate !== undefined) patch.contractDueDate = input.contractDueDate;
    if (input.monetaryAmount !== undefined) patch.monetaryAmount = input.monetaryAmount;
    if (input.resourceExchangeNote !== undefined) {
      patch.resourceExchangeNote = input.resourceExchangeNote?.trim() ?? null;
    }
    await updateDoc(doc(this.db, 'events', eventId, 'partners', partnerId), patch);
    const updated = await this.getById(eventId, partnerId);
    if (!updated) throw new Error('Partner not found after update');
    return updated;
  }

  async delete(eventId: string, partnerId: string): Promise<void> {
    await deleteDoc(doc(this.db, 'events', eventId, 'partners', partnerId));
  }

  async updateLogoOrder(eventId: string, orderedPartnerIds: string[]): Promise<void> {
    const batch = writeBatch(this.db);
    orderedPartnerIds.forEach((id, index) => {
      batch.update(doc(this.db, 'events', eventId, 'partners', id), {
        logoDisplayOrder: index,
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }
}

export class FirebasePartnerLogRepository implements PartnerLogRepository {
  constructor(private readonly db: Firestore) {}

  async listByPartner(eventId: string, partnerId: string): Promise<PartnerLog[]> {
    return new Promise((resolve, reject) => {
      const unsub = onSnapshot(
        partnerLogsCol(this.db, eventId),
        (snap) => {
          unsub();
          const logs = snap.docs
            .map((d) => mapPartnerLogDoc(eventId, d.id, snapshotData(d)))
            .filter((l) => l.partnerId === partnerId)
            .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
          resolve(logs);
        },
        reject,
      );
    });
  }

  watchByPartner(
    eventId: string,
    partnerId: string,
    listener: (logs: PartnerLog[]) => void,
  ): Unsubscribe {
    return onSnapshot(partnerLogsCol(this.db, eventId), (snap) => {
      const logs = snap.docs
        .map((d) => mapPartnerLogDoc(eventId, d.id, snapshotData(d)))
        .filter((l) => l.partnerId === partnerId)
        .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
      listener(logs);
    });
  }

  async create(
    eventId: string,
    partnerId: string,
    content: string,
    createdBy: string,
  ): Promise<PartnerLog> {
    const ref = await addDoc(partnerLogsCol(this.db, eventId), {
      partnerId,
      content: content.trim(),
      createdBy,
      createdAt: serverTimestamp(),
    });
    const list = await this.listByPartner(eventId, partnerId);
    const created = list.find((l) => l.id === ref.id);
    if (!created) throw new Error('Log not found after create');
    return created;
  }

  async delete(eventId: string, logId: string, createdBy: string): Promise<void> {
    const ref = doc(this.db, 'events', eventId, 'partnerLogs', logId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snapshotData(snap);
    if (data.createdBy !== createdBy) {
      throw new Error('Not allowed to delete this log');
    }
    await deleteDoc(ref);
  }
}
